"use client";

import Header from "@/components/layout/header";
import { Footer } from "@/components/layout/footer";
import { FAQAccordion } from "@/components/pages/faq-accordion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  BsMic as Mic,
  BsLightning as Zap,
  BsPersonCircle as UserCircle,
  BsTextLeft as Text,
  BsGear as Settings,
  BsBook as BookOpen,
  BsSend as Send,
  BsCameraVideo as Video,
} from "react-icons/bs";
import {
  AiOutlineArrowRight as ArrowRight,
  AiOutlineDownload as Download,
  AiOutlineShareAlt as Share2,
} from "react-icons/ai";
import { IoPlay as Play } from "react-icons/io5";
import { useAuth } from "@/lib/auth/auth-context";

export default function Home() {
  const { user } = useAuth();

  const handleGetStarted = () => {
    window.location.href = user ? "/dashboard" : "/auth/login";
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />

      <main>
        {/* Hero Section */}
        <section className="py-20 md:py-32 relative overflow-hidden">
          {/* BG do /public/images */}
          <div className="absolute inset-0 flex items-center justify-center opacity-30 pointer-events-none select-none">
            <img
              src="/images/podkst_hero_simple.png"
              alt="PODKST.AI Background"
              className="w-full h-full object-cover opacity-20"
              loading="eager"
              decoding="async"
            />
          </div>

          <div className="container px-4 mx-auto relative">
            <div className="flex flex-col items-center max-w-4xl mx-auto text-center">
              {/* Logo do /public (mantém animação) */}
              <img
                src="/podkst-ai-logo.svg"
                alt="PODKST.AI Icon"
                className="w-20 h-20 mb-8 animate-pulse"
                loading="eager"
                decoding="async"
              />

              <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl lg:text-7xl">
                Crie Podcasts com IA <span className="text-primary">em Segundos</span>
              </h1>

              <p className="mt-6 text-xl text-gray-400 max-w-2xl">
                Transforme ideias em episódios completos com vozes realistas em português
              </p>

              <Button
                size="lg"
                className="mt-10 px-8 py-6 gap-2 text-lg font-medium rounded-full bg-primary hover:bg-primary/90 text-black"
                onClick={handleGetStarted}
              >
                {user ? "Ir para Dashboard" : "Testar Agora Gratuitamente"}
                <ArrowRight className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </section>

        {/* Como Funciona */}
        <section id="how-it-works" className="py-20 bg-zinc-950">
          <div className="container px-4 mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">Como Funciona</h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="text-center p-6 bg-zinc-900 border-zinc-800">
                <CardContent className="space-y-4">
                  <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mx-auto">
                    <Text className="w-6 h-6 text-black" />
                  </div>
                  <h3 className="text-xl font-semibold">1. Escreva ou Descreva</h3>
                  <p className="text-gray-400">
                    Digite seu tema ou cole o texto que deseja transformar em podcast
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center p-6 bg-zinc-900 border-zinc-800">
                <CardContent className="space-y-4">
                  <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mx-auto">
                    <Settings className="w-6 h-6 text-black" />
                  </div>
                  <h3 className="text-xl font-semibold">2. Configure as Vozes</h3>
                  <p className="text-gray-400">
                    Escolha entre vozes masculinas e femininas em português brasileiro
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center p-6 bg-zinc-900 border-zinc-800">
                <CardContent className="space-y-4">
                  <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mx-auto">
                    <Play className="w-6 h-6 text-black" />
                  </div>
                  <h3 className="text-xl font-semibold">3. Gere e Compartilhe</h3>
                  <p className="text-gray-400">
                    Em segundos seu podcast estará pronto para download e compartilhamento
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Funcionalidades */}
        <section id="features" className="py-20 bg-black">
          <div className="container px-4 mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">Funcionalidades Principais</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card className="p-6 bg-zinc-900 border-zinc-800">
                <CardContent className="space-y-4">
                  <Mic className="w-8 h-8 text-primary" />
                  <h3 className="text-lg font-semibold">Vozes Naturais</h3>
                  <p className="text-gray-400 text-sm">
                    12 vozes premium em português brasileiro com tecnologia Google Cloud
                  </p>
                </CardContent>
              </Card>

              <Card className="p-6 bg-zinc-900 border-zinc-800">
                <CardContent className="space-y-4">
                  <Zap className="w-8 h-8 text-primary" />
                  <h3 className="text-lg font-semibold">Geração Rápida</h3>
                  <p className="text-gray-400 text-sm">Crie podcasts profissionais em minutos, não horas</p>
                </CardContent>
              </Card>

              <Card className="p-6 bg-zinc-900 border-zinc-800">
                <CardContent className="space-y-4">
                  <UserCircle className="w-8 h-8 text-primary" />
                  <h3 className="text-lg font-semibold">Múltiplos Personagens</h3>
                  <p className="text-gray-400 text-sm">
                    Diálogos naturais entre diferentes vozes e personagens
                  </p>
                </CardContent>
              </Card>

              <Card className="p-6 bg-zinc-900 border-zinc-800">
                <CardContent className="space-y-4">
                  <BookOpen className="w-8 h-8 text-primary" />
                  <h3 className="text-lg font-semibold">Scripts Inteligentes</h3>
                  <p className="text-gray-400 text-sm">IA gera roteiros completos baseados no seu conteúdo</p>
                </CardContent>
              </Card>

              <Card className="p-6 bg-zinc-900 border-zinc-800">
                <CardContent className="space-y-4">
                  <Download className="w-8 h-8 text-primary" />
                  <h3 className="text-lg font-semibold">Download Instantâneo</h3>
                  <p className="text-gray-400 text-sm">Baixe seus podcasts em alta qualidade MP3</p>
                </CardContent>
              </Card>

              <Card className="p-6 bg-zinc-900 border-zinc-800">
                <CardContent className="space-y-4">
                  <Share2 className="w-8 h-8 text-primary" />
                  <h3 className="text-lg font-semibold">Compartilhamento Fácil</h3>
                  <p className="text-gray-400 text-sm">
                    Compartilhe diretamente nas plataformas de podcast
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Para Quem */}
        <section id="for-who" className="py-20 bg-zinc-950">
          <div className="container px-4 mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">Ideal Para</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto">
                  <UserCircle className="w-8 h-8 text-black" />
                </div>
                <h3 className="text-lg font-semibold">Criadores de Conteúdo</h3>
                <p className="text-gray-400 text-sm">
                  Transforme seus artigos e posts em podcasts envolventes
                </p>
              </div>

              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto">
                  <BookOpen className="w-8 h-8 text-black" />
                </div>
                <h3 className="text-lg font-semibold">Educadores</h3>
                <p className="text-gray-400 text-sm">Crie materiais educacionais em formato de áudio</p>
              </div>

              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto">
                  <Send className="w-8 h-8 text-black" />
                </div>
                <h3 className="text-lg font-semibold">Empresas</h3>
                <p className="text-gray-400 text-sm">Comunicação interna e marketing de conteúdo</p>
              </div>

              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto">
                  <Video className="w-8 h-8 text-black" />
                </div>
                <h3 className="text-lg font-semibold">Podcasters</h3>
                <p className="text-gray-400 text-sm">Prototipe episódios rapidamente antes da gravação</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="py-20 bg-black">
          <div className="container px-4 mx-auto max-w-3xl">
            <h2 className="text-3xl font-bold text-center mb-12">Perguntas Frequentes</h2>
            <FAQAccordion />
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-20 bg-gradient-to-r from-primary/20 to-accent/20">
          <div className="container px-4 mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Pronto para Começar?</h2>
            <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
              Crie seu primeiro podcast com IA em menos de 5 minutos
            </p>
            <Button
              size="lg"
              className="px-8 py-6 gap-2 text-lg font-medium rounded-full bg-primary hover:bg-primary/90 text-black"
              onClick={handleGetStarted}
            >
              {user ? "Ir para Dashboard" : "Começar Agora Gratuitamente"}
              <ArrowRight className="w-5 h-5" />
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
